import { useEffect, useRef, useState } from "react";
import type { GLViewer } from "3dmol";
import { Loader2 } from "lucide-react";
import { StructureStyleSelect } from "~/components/assistant/messages/StructureStyleSelect";
import {
  applyProteinViewStyle,
  DEFAULT_PROTEIN_VIEW_STYLE,
  type ProteinViewStyle,
} from "~/components/assistant/messages/proteinViewerStyle";
import { cn } from "~/lib/utils";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import {
  artifactDownloadPath,
  type ArtifactDownloadResult,
  type ArtifactPreview,
} from "~/services/artifacts";
import { apiMutate } from "~/utils/apiClient";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";

const STRUCTURE_EXTENSIONS = new Set(["pdb", "cif", "mmcif"]);
const MAX_STRUCTURE_FILE_BYTES = 20 * 1024 * 1024;

export function isProteinStructureArtifact(preview: ArtifactPreview): boolean {
  const filename = filenameFromPreview(preview);
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return STRUCTURE_EXTENSIONS.has(ext);
}

function filenameFromPreview(preview: ArtifactPreview): string {
  const c = preview.content;
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const fileName = (c as { file_name?: unknown }).file_name;
    if (typeof fileName === "string" && fileName) {
      return fileName;
    }
  }
  return preview.filename ?? preview.artifact_name ?? "";
}

function getFormatFromFileName(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "cif" || ext === "mmcif") return "cif";
  return "pdb";
}

interface ProteinViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

const ProteinViewer = ({ preview, className }: ProteinViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<GLViewer | null>(null);
  const threeDmolRef = useRef<typeof import("3dmol") | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [style, setStyle] = useState<ProteinViewStyle>(DEFAULT_PROTEIN_VIEW_STYLE);
  const { conversationId } = useConversationContext();
  const filename = filenameFromPreview(preview);

  useEffect(() => {
    if (!containerRef.current || !conversationId || !preview.artifact_id) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    const loadStructure = async () => {
      try {
        setIsLoading(true);
        setIsReady(false);
        setError(null);

        const downloadResult = await apiMutate<ArtifactDownloadResult>(
          artifactDownloadPath(conversationId, preview.artifact_id),
          "POST",
          { format: "original" }
        );

        if (cancelled) return;

        const response = await fetch(downloadResult.download_url);
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }

        const contentLength = response.headers.get("Content-Length");
        if (contentLength && Number(contentLength) > MAX_STRUCTURE_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        const structureData = await response.text();
        if (cancelled) return;

        const $3Dmol = await import("3dmol");
        if (cancelled || !containerRef.current) return;

        if (viewerRef.current) {
          viewerRef.current.clear();
        }

        const viewer = $3Dmol.createViewer(containerRef.current, {
          backgroundColor: "white",
        });
        viewerRef.current = viewer;
        threeDmolRef.current = $3Dmol;

        const format = getFormatFromFileName(filename);
        viewer.addModel(structureData, format);
        viewer.zoomTo();
        setIsReady(true);

        resizeObserver = new ResizeObserver(() => {
          viewer.resize();
          viewer.render();
        });
        resizeObserver.observe(containerRef.current);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load protein structure:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadStructure();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (viewerRef.current) {
        viewerRef.current.clear();
        viewerRef.current = null;
      }
      threeDmolRef.current = null;
    };
  }, [conversationId, preview.artifact_id, filename]);

  // Apply the representation mode once the model is loaded, and whenever the
  // user changes it. Kept separate from loading so switching modes never
  // re-downloads the structure.
  useEffect(() => {
    if (!isReady || !viewerRef.current || !threeDmolRef.current) return;
    applyProteinViewStyle(viewerRef.current, threeDmolRef.current, style);
  }, [isReady, style]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "h-[450px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading structure...</p>
          </div>
        </div>
      )}
      {!isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <StructureStyleSelect value={style} onChange={setStyle} />
        </div>
      )}
      <div
        ref={containerRef}
        className={cn("w-full h-full rounded-md", isLoading && "invisible")}
      />
    </div>
  );
};

export const ProteinStructureArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<ProteinViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <ProteinViewer preview={preview} />
    </div>
  </ArtifactShell>
);
